import { Button, Flex, Select, Tooltip, notification } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { LuEye } from 'react-icons/lu';
import { useMutation, useQuery } from '@tanstack/react-query';
import CollectionsFilters from './CollectionsFilters';
import { LuSearch } from 'react-icons/lu';
import { TextL, TextM, TextS } from 'components/Text';
import { useNavigate } from 'react-router-dom';
import { Wallet, WalletContent, WalletStatus } from 'types/Wallet';
import { useCallback, useContext, useState } from 'react';
import { Table } from 'components/Table';
import { ActionIconButton, FiltersContainer } from './styles';
import { FilterButton } from 'components/FilterButton';
import { AiOutlineStop } from 'react-icons/ai';
import { useTheme } from 'styled-components';
import { CollectionsFiltersType } from './types';
import { normalizeBankAccount } from 'helpers/normalizers';
import { ESortingPage, PaginationInfo } from 'types/List';
import { EditIcon } from 'components/Icons/Edit';
import { WalletsService } from 'modules/debitCollections/services';
import { WalletCreation } from 'modules/debitCollections/components/WalletCreation';
import { AbilityContext } from 'modules/core/context/AbilityContext';
import { subject } from '@casl/ability';
import { ArchiveIcon } from 'components/Icons/Archive';
import { ApiError } from 'types/ApiError';
import { WalletStatusDescription } from 'constants/wallet';
import { ConfirmationModal } from 'components/ConfirmationModal';
import { queryClient } from 'network/query';
import { formatAddZerosMaskInput } from 'helpers/formaters';
import { sortTableHandler } from 'helpers/tables';
import { useIsEscrowAccountRoute } from 'modules/escrow/utils/adminRoute';
import { contaLivre } from 'modules/escrow/constants/routes';

const Collections = () => {
	const theme = useTheme();
	const navigate = useNavigate();
	const ability = useContext(AbilityContext);
	const [api, contextHolder] = notification.useNotification();

	const [paginationInfo, setPaginationInfo] = useState<PaginationInfo>({
		currentPage: 1,
		pageSize: 10,
	});
	const [isWalletEditOpen, setIsWalletEditOpen] = useState<
		Wallet | undefined
	>();
	const [sorting, setSorting] = useState<{
		order: ESortingPage;
		sort: string;
	}>({ order: ESortingPage.DESC, sort: 'created_at' });
	const [isFiltersOpen, setIsFiltersOpen] = useState<boolean>(false);
	const [isArchiveWalletModalOpen, setIsArchiveWalletModalOpen] = useState<
		string | false
	>(false);
	const [filters, setFilters] = useState<CollectionsFiltersType>(
		{} as CollectionsFiltersType,
	);

	const accountRoute = !useIsEscrowAccountRoute() ? contaLivre : 'escrow';
	const handleClearFilters = useCallback(() => {
		setFilters({} as CollectionsFiltersType);
	}, []);

	const { data, isLoading } = useQuery({
		queryKey: ['walletsList', paginationInfo, filters, sorting],
		queryFn: () =>
			WalletsService.getWallets({
				page: paginationInfo.currentPage,
				size: paginationInfo.pageSize,
				order: sorting.order,
				sort: sorting.sort,
				wallet_code: filters.wallet_code,
				name: filters.name,
				beneficiary_account: filters.beneficiary_account,
				beneficiary_document: filters.taxpayer_id,
				status: filters.status,
			}),
	});

	const { mutate: archive, isPending: isArchiving } = useMutation<
		{ id: string },
		ApiError,
		string
	>({
		mutationFn: (walletId: string) =>
			WalletsService.archiveWallet(walletId),
		onSuccess: () => {
			api.success({
				message: 'Carteira arquivada com sucesso!',
			});
			queryClient.refetchQueries({
				queryKey: ['walletsList'],
			});
		},
		onError: e => {
			console.error(e);
			api.error({
				message: 'Erro ao arquivar a carteira!',
			});
		},
	});

	const handleNavigateToDetails = (id: string) => {
		navigate(`/debit-collections/wallet/${id}/barcodes`);
	};

	const columns: ColumnsType<WalletContent> = [
		{
			title: 'Código da carteira',
			width: '15%',
			render: (wallet: WalletContent) => (
				<TextM weight="normal" style={{ textTransform: 'uppercase' }}>
					{typeof wallet.wallet_code === 'number'
						? `${formatAddZerosMaskInput(String(wallet.wallet_code))}`
						: '-'}
				</TextM>
			),
		},
		{
			title: 'Nome da carteira',
			render: (wallet: WalletContent) => (
				<TextM weight="normal">{`${wallet.name}`}</TextM>
			),
			width: '15%',
		},
		{
			title: 'Conta de destino',
			width: '13%',
			render: (wallet: WalletContent) => {
				if (wallet.account) {
					return (
						<Button
							type="link"
							style={{ padding: 0 }}
							onClick={() =>
								navigate(
									`/${accountRoute}/${wallet.account?.id}/details/info`,
								)
							}
						>
							{normalizeBankAccount(wallet.account.account)}
						</Button>
					);
				}

				return '-';
			},
		},
		{
			title: 'Status',
			width: '10%',
			render: (wallet: WalletContent) =>
				WalletStatusDescription[wallet.status],
		},
		{
			title: 'Ações',
			key: 'action',
			width: '12%',
			align: 'end',
			render: (wallet: WalletContent) => {
				const canEdit = wallet.status !== 'ARCHIVED';
				const canArchive =
					canEdit &&
					ability.can(
						'ARCHIVE',
						subject('WALLET', { accountId: wallet.account?.id }),
					);

				return (
					<Flex
						data-row-actions
						gap={4}
						justify="flex-end"
						onClick={event => event.stopPropagation()}
					>
						<Tooltip title="Visualizar">
							<ActionIconButton
								type="text"
								aria-label="Visualizar"
								disabled={isArchiving}
								icon={<LuEye size={16} />}
								onClick={() =>
									handleNavigateToDetails(wallet.id)
								}
							/>
						</Tooltip>
						{canEdit && (
							<Tooltip title="Editar">
								<ActionIconButton
									type="text"
									aria-label="Editar"
									disabled={isArchiving}
									icon={
										<EditIcon
											color="currentColor"
											size={16}
										/>
									}
									onClick={() =>
										setIsWalletEditOpen({
											id: wallet.id,
											name: wallet.name,
											account_id:
												wallet.account?.id ?? '',
											fine: wallet.fine,
											interest: wallet.interest,
											days_to_expire_after_payment:
												wallet.days_to_expire_after_payment,
											discount_modality:
												wallet.discount_modality,
											discount_value:
												wallet.discount_value,
										})
									}
								/>
							</Tooltip>
						)}
						{canArchive && (
							<Tooltip title="Arquivar">
								<ActionIconButton
									type="text"
									aria-label="Arquivar"
									disabled={isArchiving}
									icon={<ArchiveIcon color="currentColor" />}
									onClick={() =>
										setIsArchiveWalletModalOpen(wallet.id)
									}
								/>
							</Tooltip>
						)}
					</Flex>
				);
			},
		},
	];

	return (
		<>
			{contextHolder}
			<FiltersContainer>
				<div>
					<TextM style={{ marginBottom: '8px' }}>Status</TextM>
					<Select
						style={{ width: '200px', height: '32px' }}
						options={[
							{
								label: 'Todos',
								value: '',
							},
						].concat(
							Object.keys(WalletStatusDescription)
								.map(key => ({
									label: WalletStatusDescription[
										key as WalletStatus
									],
									value: key,
								}))
								.sort((a, b) => a.label.localeCompare(b.label)),
						)}
						value={filters.status ?? ''}
						onChange={value => {
							setFilters({
								...filters,
								status:
									value !== ''
										? (value as WalletStatus)
										: undefined,
							});
						}}
					/>
				</div>
				<FilterButton
					icon={<LuSearch size={18} />}
					onClick={() => setIsFiltersOpen(true)}
				>
					<TextS style={{ color: theme.white }}>Busca avançada</TextS>
				</FilterButton>
				<FilterButton
					icon={<AiOutlineStop size={18} color={theme.white} />}
					variation="secondary"
					onClick={handleClearFilters}
					disabled={Object.keys(filters).length === 0}
				>
					<TextS style={{ color: theme.white }}>Limpar filtros</TextS>
				</FilterButton>
			</FiltersContainer>
			<Table
				columns={columns}
				rowKey={record => record.id}
				dataSource={data?.content}
				loading={isLoading}
				onChange={(_, __, sorter) =>
					sortTableHandler(sorter, setSorting)
				}
				pagination={{
					total: data?.total_elements || 0,
					showTotal(total) {
						const currentSize =
							paginationInfo.currentPage *
							paginationInfo.pageSize;
						return `${currentSize > total ? total : currentSize} de ${total}`;
					},
					pageSizeOptions: ['10', '30', '60', '90'],
					showSizeChanger: true,
					pageSize: paginationInfo.pageSize,
					current: paginationInfo.currentPage,
					onChange(page, pageSize) {
						setPaginationInfo({
							currentPage: page,
							pageSize,
						});
					},
				}}
				onRow={(record: WalletContent) => ({
					onClick: event => {
						const target = event.target as HTMLElement;
						if (target.closest('[data-row-actions]')) return;
						handleNavigateToDetails(record.id);
					},
				})}
			/>
			<CollectionsFilters
				isOpen={isFiltersOpen}
				onApply={filters => {
					setFilters(filters);
					setIsFiltersOpen(false);
				}}
				onClose={() => setIsFiltersOpen(false)}
				filters={filters}
			/>
			<WalletCreation
				isOpen={isWalletEditOpen !== undefined}
				wallet={isWalletEditOpen}
				onClose={() => setIsWalletEditOpen(undefined)}
			/>
			<ConfirmationModal
				isOpen={isArchiveWalletModalOpen !== false}
				danger={true}
				title="Arquivar carteira"
				confirmText="Arquivar carteira"
				cancelText="Cancelar"
				isLoading={isArchiving}
				onConfirm={() => {
					if (isArchiveWalletModalOpen !== false) {
						archive(isArchiveWalletModalOpen);
					}
					setIsArchiveWalletModalOpen(false);
				}}
				onCancel={() => setIsArchiveWalletModalOpen(false)}
				onClose={() => setIsArchiveWalletModalOpen(false)}
			>
				<TextL>Você tem certeza que deseja arquivar a carteira?</TextL>
				<TextM weight="normal">
					Essa ação não poderá ser desfeita.
				</TextM>
			</ConfirmationModal>
		</>
	);
};

export default Collections;
